import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../shadcn/button";
import { cn } from "../../../lib/cn";
import { dayLabel } from "./nightClock";

export type NightPickerProps = {
  /** Seven nights, oldest first. Never one after last night. */
  days: string[];
  selected: string;
  savedDays: Set<string>;
  onSelect(day: string): void;
  onPrev(): void;
  onNext(): void;
  nextDisabled: boolean;
  /** Last night is not known yet (the week is loading): placeholders, no navigation. */
  loading?: boolean;
};

/** Seven nights with a dot on the saved ones (mockup-v4:250-256, 495-507). */
export function NightPicker({ days, selected, savedDays, onSelect, onPrev, onNext, nextDisabled, loading }: NightPickerProps) {
  return (
    <div
      role="group"
      aria-label="Choose a night"
      aria-busy={loading || undefined}
      className="flex items-center gap-1 rounded-[12px] border border-neutral-200 bg-white p-1 max-[859px]:w-full max-[859px]:justify-between"
    >
      <Button variant="ghost" size="icon" aria-label="Earlier nights" disabled={loading} onClick={onPrev} className="disabled:opacity-30">
        <ChevronLeft size={16} aria-hidden />
      </Button>
      <div className="flex flex-1 gap-0.5">
        {loading
          ? Array.from({ length: 7 }, (_, i) => (
              <span key={i} className="h-[52px] w-11 rounded-lg bg-neutral-100 max-[859px]:w-auto max-[859px]:flex-1" />
            ))
          : days.map((day) => {
              const on = day === selected;
              const saved = savedDays.has(day);
              const label = dayLabel(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-label={`Night of ${label}`}
                  aria-pressed={on}
                  data-saved={saved ? "true" : "false"}
                  onClick={() => onSelect(day)}
                  className={cn(
                    "h-[52px] w-11 rounded-lg text-center transition-colors duration-150 ease-soft focus-visible:outline-none focus-visible:shadow-ring max-[859px]:w-auto max-[859px]:flex-1",
                    on ? "bg-neutral-950 text-white" : "hover:bg-neutral-50"
                  )}
                >
                  <div className={cn("text-[11px] leading-4", on ? "text-neutral-300" : "text-neutral-500")}>
                    {label.split(" ")[0]}
                  </div>
                  <div className="text-[15px] font-medium">{Number(day.slice(8))}</div>
                  <div
                    className={cn(
                      "mx-auto mt-0.5 h-1 w-1 rounded-full",
                      saved ? (on ? "bg-green-300" : "bg-green-600") : "bg-transparent"
                    )}
                  />
                </button>
              );
            })}
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Later nights"
        disabled={loading || nextDisabled}
        onClick={onNext}
        className="disabled:opacity-30"
      >
        <ChevronRight size={16} aria-hidden />
      </Button>
    </div>
  );
}
